import { useAppDispatch } from "hooks/hooks";
import { useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import {
  getConstructionStatistics,
  selectState as constructionSelectState,
} from "../slices/ConstructionStatistics.slice";
import {
  getOtherProjectsStatistics,
  selectState as otherProjectsSelectState,
} from "../slices/OtherProjectsStatistics.slice";
import "../styles/main.scss";
import Loader from "components/Loader/Loader";
import DateSelector from "Dashboard/components/DateSelector";
import { selectState as mainSelectState } from "../slices/main.slice";
import { mapProjectCategories, FormatNumber } from "../services/statistics.service";
import { FormatDate } from "utils/dateUtils";

const statisticsByType = {
  construction: {
    title: "Građevinski Projekti",
    getStatistics: getConstructionStatistics,
    selectState: constructionSelectState,
  },
  other: {
    title: "Ostali projekti",
    getStatistics: getOtherProjectsStatistics,
    selectState: otherProjectsSelectState,
  },
};

interface ProjectsStatisticsProps {
  type?: keyof typeof statisticsByType;
}

export const ProjectsStatistics = ({ type = "construction" }: ProjectsStatisticsProps) => {
  const { title, getStatistics, selectState } = statisticsByType[type];
  const { statistics, loading } = useSelector(selectState);

  const { mainDateFrom } = useSelector(mainSelectState);

  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [, setDateTo] = useState<Date | null>(null);

  const dispatch = useAppDispatch();
  const [projectsArray, setProjectsArray] = useState<number[]>([]);

  useEffect(() => {
    if (dateFrom != null) {
      dispatch(
        getStatistics({
          year: dateFrom.getFullYear(),
          projectIds: projectsArray,
        })
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, projectsArray]);

  const selectDate = useCallback((data: { dateFrom: Date; dateTo: Date }) => {
    setProjectsArray([]);
    setDateFrom(data.dateFrom);
    setDateTo(data.dateTo);
  }, []);

  const getHeight = (num: number | undefined) => {
    if (num === 0) {
      return 0;
    }
    if (num) {
      const result = 50 / (getHighest() / num);
      if (Math.round(result) === 0 && num > 0) {
        return 1;
      }
      return Math.round(result);
    }
  };

  const getHighest = () => {
    let highest = 0;
    const array = [
      statistics?.earnings,
      statistics?.moneyReceived,
      statistics?.moneyPaidOff,
      statistics?.costs,
      statistics?.costsInCash,
      statistics?.incidents,
    ];
    array.forEach((element) => {
      if (element && Math.abs(element) > highest) {
        highest = Math.abs(element);
      }
    });
    return highest;
  };

  const getPosition = (num: number | undefined) => {
    if (num) {
      if (num > 0) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  };

  const pushToArray = (index: number) => {
    let newProjectsArray: number[];
    if (projectsArray.includes(index)) {
      newProjectsArray = projectsArray.filter((element) => element !== index);
    } else {
      newProjectsArray = [...projectsArray, index];
    }
    setProjectsArray(newProjectsArray);
  };

  return (
    <div className="mainDiv">
      <div className="header green">{title}</div>
      <div className="graphs  loading-container">
        {loading && <Loader />}
        <div className="graphs-header">
          <span>
            <div className="color1"></div>Zarada
          </span>
          <span>
            <div className="color2"></div>Para dobili
          </span>
          <span>
            <div className="color3"></div>Para isplatili
          </span>
          <span>
            <div className="color4"></div>Trošak
          </span>
          <span>
            <div className="color5"></div>Troškovi keš
          </span>
          <span>
            <div className="color6"></div>Incidenti
          </span>
        </div>
        <div className="graphs-content">
          <span className={`graph graph-${getHeight(statistics?.earnings)}`}>
            {getPosition(statistics?.earnings) && (
              <p>{FormatNumber(statistics?.earnings)} rsd</p>
            )}
            <div className="color1"></div>
            {!getPosition(statistics?.earnings) && (
              <p>{FormatNumber(statistics?.earnings)} rsd</p>
            )}
          </span>
          <span
            className={`graph graph-${getHeight(statistics?.moneyReceived)}`}
          >
            {getPosition(statistics?.moneyReceived) && (
              <p>{FormatNumber(statistics?.moneyReceived)} rsd</p>
            )}
            <div className="color2"></div>
            {!getPosition(statistics?.moneyReceived) && (
              <p>{FormatNumber(statistics?.moneyReceived)} rsd</p>
            )}
          </span>
          <span
            className={`graph graph-${getHeight(statistics?.moneyPaidOff)}`}
          >
            {getPosition(statistics?.moneyPaidOff) && (
              <p>{FormatNumber(statistics?.moneyPaidOff)} rsd</p>
            )}
            <div className="color3"></div>
            {!getPosition(statistics?.moneyPaidOff) && (
              <p>{FormatNumber(statistics?.moneyPaidOff)} rsd</p>
            )}
          </span>
          <span className={`graph graph-${getHeight(statistics?.costs)}`}>
            {getPosition(statistics?.costs) && <p>{FormatNumber(statistics?.costs)} rsd</p>}
            <div className="color4"></div>
            {!getPosition(statistics?.costs) && <p>{FormatNumber(statistics?.costs)} rsd</p>}
          </span>
          <span className={`graph graph-${getHeight(statistics?.costsInCash)}`}>
            {getPosition(statistics?.costsInCash) && (
              <p>{FormatNumber(statistics?.costsInCash)} rsd</p>
            )}
            <div className="color5"></div>
            {!getPosition(statistics?.costsInCash) && (
              <p>{FormatNumber(statistics?.costsInCash)} rsd</p>
            )}
          </span>
          <span className={`graph graph-${getHeight(statistics?.incidents)}`}>
            {getPosition(statistics?.incidents) && (
              <p>{FormatNumber(statistics?.incidents)} rsd</p>
            )}
            <div className="color6"></div>
            {!getPosition(statistics?.incidents) && (
              <p>{FormatNumber(statistics?.incidents)} rsd</p>
            )}
          </span>
        </div>
      </div>
      <table className="statistics-table-header">
        <colgroup>
          <col className="w5" />
          <col className="w24" />
          <col className="w11" />
          <col className="w10" />
          <col className="w10" />
          <col className="w10" />
          <col className="w11" />
          <col className="w11" />
        </colgroup>
        <thead>
          <tr>
            <th className="selector">
              <DateSelector
                selectedYear={mainDateFrom?.getFullYear()}
                showMonth={false}
                lastYearNumbers={5}
                selectDate={selectDate}
              />
            </th>
            <th className="header-cell">naziv projekta</th>
            <th className="header-cell">kategorija</th>
            <th>datum ugovora</th>
            <th>početak radova</th>
            <th className="header-cell">krajnji rok</th>
            <th className="header-cell">završio</th>
            <th>zaradio</th>
          </tr>
        </thead>
      </table>
      <table className="statistics-table-content loading-container">
        <colgroup>
          <col className="w5" />
          <col className="w24" />
          <col className="w11" />
          <col className="w10" />
          <col className="w10" />
          <col className="w10" />
          <col className="w11" />
          <col className="w11" />
        </colgroup>
        <tbody>
          {statistics?.projects &&
            statistics?.projects.map((project, index) => (
              <tr key={index}>
                <td className="firstBtn">
                  <div>
                    <input
                      onChange={() => pushToArray(project.projectId)}
                      checked={projectsArray.includes(project.projectId)}
                      type="checkbox"
                    />
                  </div>
                </td>
                <td>
                  <p>{project.projectName}</p>
                </td>
                <td>{mapProjectCategories(project.type.name)}</td>
                <td>{FormatDate(project.contractDate)}</td>
                <td>{FormatDate(project.startDate)}</td>
                <td>{FormatDate(project.dueDate)}</td>
                <td>{FormatDate(project.finishDate)}</td>
                <td>{FormatNumber(project.earnings)} rsd</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};
